import { numericFactCheckNotApplicable } from '../../services/reportFactCheck';
/**
 * QualityPanel — Report Insights tray, Quality tab (Section C1).
 *
 * Pure-presentation surface for the four quality signals carried on a
 * `SiteRagReport`:
 *
 *   1. AI Review score      — `report.qualityReview`
 *   2. Fact-check breakdown — `report.factCheck`
 *   3. Evidence confidence  — `report.evidenceConfidence`
 *   4. SED integrity        — `report.sedCompleteness/Integrity/Gaps/Anomalies`
 *
 * The whole panel returns `null` only when every one of those fields
 * is absent; otherwise each block renders either its data or a
 * one-line empty state, so the structure stays consistent across
 * report types.
 */
import React, { useState } from 'react';
import type { SiteRagReport } from '../../types/siteRag';
import { CountReconciliationInfo } from './CountReconciliationInfo';
import { reevaluateStampedHoldFindings } from '../../lib/audit/reevaluateQualityHold';
import { openAuditFindings } from '../../lib/audit/openAuditFlag';
import {
  extractUnresolvedHighQualityReviewIssues,
  extractUnresolvedSanityFindings,
} from '../../lib/decisionAnalysis/fromReport';

interface Props {
  report: SiteRagReport;
  /**
   * ITT-3 — fact-check retry CTA. When wired, the Fact-check empty
   * state shows a "Retry fact-check" button that calls this callback.
   * The button is hidden when the prop is omitted so legacy callers
   * keep the silent-empty-state behaviour and we never advertise a
   * retry that no parent has implemented. Returns a promise so the
   * panel can render its loading state — the panel does not call the
   * orchestrator directly.
   */
  onRetryFactCheck?: (reportId: string) => Promise<void>;
  /** Cross-tab navigation hook for ITT-4. When wired, the "issues
   *  flagged" badge becomes a clickable chip that opens the Findings
   *  tab pre-filtered to the issue bucket. Hidden when omitted. */
  onJumpToFindings?: (bucket: 'issues' | 'verified' | 'other' | 'all') => void;
  /** Live unsettled truth question count after operator attestations / SED amendments. */
  openTruthQuestionsCount?: number;
}

const BLOCK_CLASS = 'border border-border bg-state-hover p-3 space-y-2';
const HEADING_CLASS = 'text-xs font-bold uppercase tracking-widest text-text-muted';
const EMPTY_CLASS = 'text-[11px] text-text-muted';

/** 0-100 progress bar. Neutral fill — the surrounding numbers carry
 *  the colour signal where one is wanted. */
function ScoreBar({ value, max = 100, fillClass = 'bg-primary' }: { value: number; max?: number; fillClass?: string }) {
  const pct = Math.max(0, Math.min(100, max === 0 ? 0 : (value / max) * 100));
  return (
    <div className="h-1.5 w-full bg-surface-3" aria-hidden="true">
      <div className={`h-full ${fillClass}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Green ≥0.85, amber 0.65–0.85, red below — for the fact-check score. */
function scoreFillClass(score: number): string {
  if (score >= 0.85) return 'bg-success';
  if (score >= 0.65) return 'bg-warning';
  return 'bg-danger';
}
function scoreTextClass(score: number): string {
  if (score >= 0.85) return 'text-success';
  if (score >= 0.65) return 'text-warning';
  return 'text-danger';
}

function pct(value: number): string {
  return `${Math.round(value * 100)}%`;
}

/** Bulleted list capped at `max` with a show-more/less toggle. */
function TruncatedList({ items, label, max = 5 }: { items: string[]; label: string; max?: number }) {
  const [expanded, setExpanded] = useState(false);
  if (items.length === 0) return null;
  const shown = expanded ? items : items.slice(0, max);
  return (
    <div className="space-y-1">
      <div className="text-[11px] font-bold uppercase tracking-widest text-text-muted">{label}</div>
      <ul className="text-[11px] text-text-secondary space-y-0.5 list-disc pl-4">
        {shown.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
      {items.length > max && (
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          className="type-meta text-primary hover:text-primary/80 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
        >
          {expanded ? 'Show less' : `Show ${items.length - max} more`}
        </button>
      )}
    </div>
  );
}

function SubScore({ label, value }: { label: string; value: number }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-[11px] text-text-secondary">
        <span className="uppercase tracking-widest">{label}</span>
        <span className="font-bold text-text-primary tabular-nums">{Math.round(value)}</span>
      </div>
      <ScoreBar value={value} />
    </div>
  );
}

export function QualityPanel({ report, onRetryFactCheck, onJumpToFindings, openTruthQuestionsCount }: Props) {
  const { qualityReview, factCheck, evidenceConfidence, reviewTrace } = report;
  const hasQualityReview = qualityReview != null;
  const hasSystemReviewFailure =
    !qualityReview &&
    reviewTrace != null &&
    reviewTrace.iterationsRun === 0 &&
    (reviewTrace.earlyExited === true || reviewTrace.finalScore === 0);
  const hasFactCheck = factCheck != null;
  const hasEvidence = evidenceConfidence != null;
  const sedGaps = report.sedGaps ?? [];
  const sedAnomalies = report.sedAnomalies ?? [];
  const hasSed =
    report.sedCompleteness != null ||
    report.sedIntegrity != null ||
    sedGaps.length > 0 ||
    sedAnomalies.length > 0;

  // ITT-3 — fact-check retry state. Live, throttled per mount; the
  // parent owns the persistence + rate-limit guard. We keep a tiny
  // last-error string so the user gets a reason if the retry itself
  // fails (e.g. "cannot retry while a generation is in flight" — a
  // Risk R3 mitigation the parent should enforce). On success the
  // parent re-renders us with a populated `factCheck`, which falls
  // back through the normal data branch below.
  //
  // These hooks must run unconditionally on every render — they are
  // declared above the early return below so React always sees the
  // same hook order (avoids React error #300).
  const [retryInFlight, setRetryInFlight] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  const readiness = (report as {
    analysisReadiness?: {
      state?: string;
      canUseForDecision?: boolean;
      qualityReady?: boolean;
      blockerSummary?: string;
      reasons?: Array<{ code?: string; message?: string }>;
    };
  }).analysisReadiness;
  const rawHoldFindings = report.qualityHoldFindings ?? [];
  const unresolvedSanityFindings = React.useMemo(
    () => extractUnresolvedSanityFindings(report),
    [report],
  );
  const unresolvedHighReviewIssues = React.useMemo(
    () => extractUnresolvedHighQualityReviewIssues(report),
    [report],
  );
  const hasUnresolvedAuditFindings =
    unresolvedSanityFindings.length > 0 || unresolvedHighReviewIssues.length > 0;
  const holdFindings = React.useMemo(
    () => {
      const evaluated =
        report.qaFindings || report.reportType === 'feasibility'
          ? reevaluateStampedHoldFindings(report, rawHoldFindings).findings
          : rawHoldFindings;
      if (evaluated.length > 0) {
        if (rawHoldFindings.length >= 20 && report.qaFindings) {
          return Array.from(new Set([...evaluated, ...openAuditFindings(report).findings]));
        }
        return evaluated;
      }
      if (report.qualityHold === 'needs-revision') {
        return [...openAuditFindings(report).findings];
      }
      if (hasUnresolvedAuditFindings) {
        return [
          ...unresolvedSanityFindings.map((f) => `${f.section}: ${f.finding}`),
          ...unresolvedHighReviewIssues.map((i) => `${i.section}: ${i.text}`),
        ];
      }
      return evaluated;
    },
    [report, rawHoldFindings, hasUnresolvedAuditFindings, unresolvedSanityFindings, unresolvedHighReviewIssues],
  );
  const storedTruthCount = (report.qaFindings?.truthQuestions ?? []).length;
  const openTruthCount = typeof openTruthQuestionsCount === 'number'
    ? openTruthQuestionsCount
    : storedTruthCount;
  const allStampsOrTruthSettled =
    !hasUnresolvedAuditFindings &&
    (rawHoldFindings.length > 0 || storedTruthCount > 0 || (report.appliedCorrectionsSummary?.items?.length ?? 0) > 0) &&
    report.qualityHold !== 'needs-revision' &&
    holdFindings.length === 0 &&
    openTruthCount === 0 &&
    (report.qualityReview?.verdict !== 'needs-revision' || (report.qualityReview?.issues?.length ?? 0) === 0);
  const readinessIsDataGapOnlyOnFeasibility =
    !hasUnresolvedAuditFindings &&
    report.reportType === 'feasibility' &&
    report.qualityHold !== 'needs-revision' &&
    holdFindings.length === 0 &&
    openTruthCount === 0 &&
    (readiness?.qualityReady === true ||
      (Array.isArray(readiness?.reasons) &&
        readiness.reasons.length > 0 &&
        readiness.reasons.every((r) => r.code === 'data-inadequate')));
  const isQualityHeld =
    hasUnresolvedAuditFindings ||
    (!allStampsOrTruthSettled &&
      !readinessIsDataGapOnlyOnFeasibility &&
      (report.qualityHold === 'needs-revision' ||
        readiness?.state === 'held' ||
        readiness?.canUseForDecision === false ||
        holdFindings.length > 0 ||
        openTruthCount > 0));
  const hasFeasibilityStageWarnings =
    !isQualityHeld &&
    report.reportType === 'feasibility' &&
    (rawHoldFindings.length > 0 ||
      storedTruthCount > 0 ||
      readinessIsDataGapOnlyOnFeasibility ||
      (report.dataGaps?.length ?? 0) > 0 ||
      (report.appliedCorrectionsSummary?.items?.length ?? 0) > 0 ||
      (report.qaFindings?.caveatHonesty?.findings?.length ?? 0) > 0);
  const hasFactContradictions = (factCheck?.contradictions?.length ?? 0) > 0;
  const effectivePass =
    qualityReview?.verdict === 'pass' && !isQualityHeld && !hasFactContradictions;

  // The panel is meaningful only when at least one signal is present.
  if (!hasQualityReview && !hasSystemReviewFailure && !hasFactCheck && !hasEvidence && !hasSed && !isQualityHeld) return null;

  const factVerifiedTotal = hasFactCheck ? factCheck.completion?.applicableChecks ?? factCheck.verified + factCheck.unverified : 0;

  const triggerRetry = async () => {
    if (!onRetryFactCheck || retryInFlight) return;
    setRetryError(null);
    setRetryInFlight(true);
    try {
      await onRetryFactCheck(report.id);
    } catch (err) {
      setRetryError(err instanceof Error ? err.message : String(err));
    } finally {
      setRetryInFlight(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="quality-panel">
      {isQualityHeld && (
        <section
          className="rounded-lg border border-danger/40 bg-danger/10 p-3 space-y-1.5"
          data-testid="quality-panel-hold-banner"
          role="status"
        >
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-xs font-bold uppercase tracking-widest text-danger">
              Authoritative Quality Hold — Needs Revision
            </h3>
            <span className="type-section px-1.5 py-0.5 border bg-danger/15 text-danger border-danger/30">
              Held · Not for decision use
            </span>
          </div>
          <p className="text-[11px] text-text-secondary">
            {readiness?.blockerSummary ??
              (openTruthCount > 0
                ? `${openTruthCount} statement${openTruthCount === 1 ? '' : 's'} require evidence confirmation before decision use.`
                : 'Deterministic audit holds remain open on this report revision.')}
          </p>
          {holdFindings.length > 0 && (
            <TruncatedList items={holdFindings} label="Active hold findings" />
          )}
        </section>
      )}
      {hasFeasibilityStageWarnings && (
        <section
          className="rounded-lg border border-warning/40 bg-warning/10 p-3 space-y-1"
          data-testid="quality-panel-feasibility-readiness"
          role="status"
        >
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-xs font-bold uppercase tracking-widest text-warning">
              Feasibility-Stage Issuance Ready · Planning Caveats Disclosed
            </h3>
            <span className="type-section px-1.5 py-0.5 border bg-success/15 text-success border-success/30">
              Feasibility Ready
            </span>
          </div>
          <p className="text-[11px] text-text-secondary">
            Clearly labelled estimates, planning assumptions, and identified data gaps are disclosed as stage-appropriate caveats and do not block feasibility issuance. Later investment, utility, and design approvals remain subject to next-stage studies.
          </p>
        </section>
      )}
      {/* ── Block 1 — AI Review score ─────────────────────────── */}
      <section className={BLOCK_CLASS} data-testid="quality-panel-ai-review">
        <div className="flex items-center justify-between gap-2">
          <h3 className={HEADING_CLASS}>AI Review</h3>
          {qualityReview ? (
            <span
              className={`type-section px-1.5 py-0.5 border ${
                effectivePass
                  ? 'bg-success/15 text-success border-success/30'
                  : isQualityHeld
                    ? 'bg-danger/15 text-danger border-danger/30'
                    : 'bg-warning/15 text-warning border-warning/30'
              }`}
            >
              {effectivePass
                ? 'Pass'
                : isQualityHeld
                  ? 'Held — Needs revision'
                  : 'Needs review'}
            </span>
          ) : hasSystemReviewFailure ? (
            <span
              className="type-section px-1.5 py-0.5 border bg-danger/15 text-danger border-danger/30"
              data-testid="quality-panel-system-review-badge"
            >
              System review failure
            </span>
          ) : null}
        </div>
        {qualityReview ? (
          <>
            <div className="flex items-baseline gap-1.5">
              <span className="text-lg font-bold text-text-primary tabular-nums">{qualityReview.score}</span>
              <span className="text-[11px] text-text-muted">/ 100 overall</span>
            </div>
            {qualityReview.verdict === 'pass' && !effectivePass && (
              <p className="text-[11px] text-warning" data-testid="quality-panel-ai-override-note">
                Draft rubric score only — overridden by authoritative quality hold / fact-check findings.
              </p>
            )}
            <div className="space-y-2">
              <SubScore label="Accuracy" value={qualityReview.accuracy} />
              <SubScore label="Citations" value={qualityReview.citations} />
              <SubScore label="Completeness" value={qualityReview.completeness} />
            </div>
            {/* ITT-4 — cross-link "issues flagged" to the Findings
                 tab pre-filtered to the issue bucket. When the parent
                 hasn't wired `onJumpToFindings`, falls back to the
                 plain text so the existing readout is preserved.
                 The info icon disclosure pins the three-count
                 reconciliation tooltip next to the chip. */}
            <div className="flex items-center gap-1 text-[11px] text-text-secondary">
              {onJumpToFindings ? (
                <button
                  type="button"
                  onClick={() => onJumpToFindings('issues')}
                  className="hover:text-text-primary underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
                  data-testid="quality-panel-issue-jump"
                >
                  {qualityReview.issueCount} issue{qualityReview.issueCount === 1 ? '' : 's'} flagged →
                </button>
              ) : (
                <span>
                  {qualityReview.issueCount} issue{qualityReview.issueCount === 1 ? '' : 's'} flagged
                </span>
              )}
              <CountReconciliationInfo surface="quality" />
            </div>
          </>
        ) : hasSystemReviewFailure && reviewTrace ? (
          <div className="space-y-2" data-testid="quality-panel-system-review-failure">
            <p className="text-[11px] font-medium text-danger">
              <span aria-hidden="true">⚠ </span>
              System review failure: automated AI quality review exited early before completing an evaluation pass (iterationsRun: {reviewTrace.iterationsRun}, earlyExited: {String(Boolean(reviewTrace.earlyExited))}, finalScore: {reviewTrace.finalScore}).
            </p>
            <p className={EMPTY_CLASS}>
              This is a platform review-stage execution failure — do not attest report claims to clear a missing review score. Use the system retry action to re-evaluate the report.
            </p>
            {onRetryFactCheck && (
              <button
                type="button"
                onClick={triggerRetry}
                disabled={retryInFlight}
                data-testid="quality-panel-system-review-retry"
                className="text-[11px] font-bold uppercase tracking-widest px-2 py-1 rounded-md border border-primary/40 text-primary hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
                aria-busy={retryInFlight}
              >
                {retryInFlight ? 'Retrying…' : 'Retry system review & audit'}
              </button>
            )}
          </div>
        ) : (
          <p className={EMPTY_CLASS}>AI quality review has not run for this report.</p>
        )}
      </section>

      {/* ── Block 2 — Fact-check breakdown ────────────────────── */}
      <section className={BLOCK_CLASS} data-testid="quality-panel-fact-check">
        <h3 className={HEADING_CLASS}>Fact-check</h3>
        {factCheck ? (
          <>
            {factCheck.completion && <p className={EMPTY_CLASS}>Completed numeric consistency check against recorded inputs. This does not verify public sentiment or source completeness.</p>}
            {numericFactCheckNotApplicable(factCheck) ? <p className={EMPTY_CLASS}>No applicable numeric checks in this narrative.</p> : <>
            <div className="flex items-baseline justify-between gap-2 text-[11px] text-text-secondary">
              <span>
                <span className="font-bold text-text-primary tabular-nums">{factCheck.verified}</span>
                {' / '}
                <span className="tabular-nums">{factVerifiedTotal}</span> {factCheck.completion ? 'numeric checks match recorded inputs' : 'claims verified'}
              </span>
              <span className={`font-bold tabular-nums ${scoreTextClass(factCheck.score)}`}>{pct(factCheck.score)}</span>
            </div>
            <ScoreBar value={factCheck.score} max={1} fillClass={scoreFillClass(factCheck.score)} />
            </>}
            {factCheck.contradictions.length > 0 ? (
              <TruncatedList items={factCheck.contradictions} label="Contradictions" />
            ) : (
              <p className={EMPTY_CLASS}>No contradictions surfaced.</p>
            )}
          </>
        ) : (
          /* ITT-3 — surface the failure with a retry CTA instead of a
             silent message. The Retry button is only rendered when the
             parent has wired `onRetryFactCheck`; otherwise we fall back
             to the legacy empty-state copy so no parent is forced to
             implement a retry endpoint to keep this panel working. */
          <div className="space-y-2" data-testid="quality-panel-fact-check-failure">
            <p className={EMPTY_CLASS}>
              <span aria-hidden="true">⚠ </span>
              Fact-check did not complete on this report.
            </p>
            {retryError && (
              <p
                className="text-[11px] text-danger"
                data-testid="quality-panel-fact-check-retry-error"
                role="status"
              >
                Retry failed: {retryError}
              </p>
            )}
            {onRetryFactCheck && (
              <button
                type="button"
                onClick={triggerRetry}
                disabled={retryInFlight}
                data-testid="retry-fact-check"
                className="text-[11px] font-bold uppercase tracking-widest px-2 py-1 rounded-md border border-primary/40 text-primary hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
                aria-busy={retryInFlight}
              >
                {retryInFlight ? 'Retrying…' : 'Retry fact-check'}
              </button>
            )}
          </div>
        )}
      </section>

      {/* ── Block 3 — Evidence confidence ─────────────────────── */}
      {/* Measures ONE thing: the report-confidence CAP applied when critical
          document slots are unfilled. It is NOT overall evidence coverage —
          the old "Evidence confidence / 100%" framing read as "evidence is
          complete" while Site evidence showed open gaps (William,
          2026-07-28). Name the cap and point at the real coverage surface. */}
      <section className={BLOCK_CLASS} data-testid="quality-panel-evidence">
        <h3 className={HEADING_CLASS}>Critical-document ceiling</h3>
        {evidenceConfidence ? (
          <>
            <div className="flex items-baseline gap-1.5">
              <span className="text-lg font-bold text-text-primary tabular-nums">{pct(evidenceConfidence.cap)}</span>
              <span className="text-[11px] text-text-muted">report-confidence cap from critical documents</span>
            </div>
            <ScoreBar value={evidenceConfidence.cap} max={1} fillClass={scoreFillClass(evidenceConfidence.cap)} />
            {evidenceConfidence.missingCriticalIds.length > 0 ? (
              <div className="space-y-1">
                <div className="text-[11px] font-bold uppercase tracking-widest text-text-muted">Missing critical documents</div>
                <div className="flex flex-wrap gap-1.5">
                  {evidenceConfidence.missingCriticalIds.map((id, i) => (
                    <span
                      key={id}
                      className="type-meta px-1.5 py-0.5 bg-warning/15 text-warning rounded-md border border-warning/30"
                    >
                      {evidenceConfidence.missingCriticalLabels[i] ?? id}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <p className={EMPTY_CLASS}>
                All critical document slots are filled, so no cap applies.
                Full evidence coverage (all fields and gaps) lives on the
                Site evidence tab.
              </p>
            )}
          </>
        ) : (
          <p className={EMPTY_CLASS}>No evidence-confidence data for this report.</p>
        )}
      </section>

      {/* ── Block 4 — SED integrity ───────────────────────────── */}
      <section className={BLOCK_CLASS} data-testid="quality-panel-sed">
        <h3 className={HEADING_CLASS}>SED integrity</h3>
        {hasSed ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <div className="text-[11px] uppercase tracking-widest text-text-secondary">Completeness</div>
                <div className="text-base font-bold text-text-primary tabular-nums">
                  {report.sedCompleteness != null ? pct(report.sedCompleteness) : '—'}
                </div>
                {report.sedCompleteness != null && <ScoreBar value={report.sedCompleteness} max={1} />}
              </div>
              <div className="space-y-1">
                <div className="text-[11px] uppercase tracking-widest text-text-secondary">Integrity</div>
                <div className="text-base font-bold text-text-primary tabular-nums">
                  {report.sedIntegrity != null ? pct(report.sedIntegrity) : '—'}
                </div>
                {report.sedIntegrity != null && <ScoreBar value={report.sedIntegrity} max={1} />}
              </div>
            </div>
            <TruncatedList items={sedGaps} label="Gaps" />
            <TruncatedList items={sedAnomalies} label="Anomalies" />
          </>
        ) : (
          <p className={EMPTY_CLASS}>No SED integrity data for this report.</p>
        )}
      </section>
    </div>
  );
}
